import React, { useState, useEffect, useRef } from 'react';
import { X, Palette, Heart, Save, Trash2, Upload, Image, Search, Copy, Check, ChevronDown, ChevronRight, PanelRightClose, PanelRight } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || '';

// ============================================================================
// COLOR CONVERSION UTILITIES
// ============================================================================

const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

const rgbToLab = (r, g, b) => {
  // Convert RGB to XYZ
  let rr = r / 255, gg = g / 255, bb = b / 255;
  rr = rr > 0.04045 ? Math.pow((rr + 0.055) / 1.055, 2.4) : rr / 12.92;
  gg = gg > 0.04045 ? Math.pow((gg + 0.055) / 1.055, 2.4) : gg / 12.92;
  bb = bb > 0.04045 ? Math.pow((bb + 0.055) / 1.055, 2.4) : bb / 12.92;
  
  const x = (rr * 0.4124564 + gg * 0.3575761 + bb * 0.1804375) / 0.95047;
  const y = (rr * 0.2126729 + gg * 0.7151522 + bb * 0.0721750);
  const z = (rr * 0.0193339 + gg * 0.1191920 + bb * 0.9503041) / 1.08883;
  
  const fx = x > 0.008856 ? Math.pow(x, 1/3) : (7.787 * x) + 16/116;
  const fy = y > 0.008856 ? Math.pow(y, 1/3) : (7.787 * y) + 16/116;
  const fz = z > 0.008856 ? Math.pow(z, 1/3) : (7.787 * z) + 16/116;
  
  return {
    l: (116 * fy) - 16,
    a: 500 * (fx - fy),
    b: 200 * (fy - fz)
  };
};

const deltaE = (lab1, lab2) => {
  return Math.sqrt(
    Math.pow(lab1.l - lab2.l, 2) +
    Math.pow(lab1.a - lab2.a, 2) +
    Math.pow(lab1.b - lab2.b, 2)
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function ColorTools({ isOpen, onClose, canvasColors = [], userEmail, craftType = null }) {
  const [activeTab, setActiveTab] = useState('threads');
  const [copiedColor, setCopiedColor] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Craft-aware brand routing (pencils, paint, glass, etc.)
  const [craftLibraries, setCraftLibraries] = useState([]); // [{category, label, brands: [...]}]
  const [materialCategory, setMaterialCategory] = useState(''); // currently selected category
  const [materialBrand, setMaterialBrand] = useState('');
  const [materialLibrary, setMaterialLibrary] = useState([]); // colors[] of the selected brand

  // Thread matching state
  const [threadBrand, setThreadBrand] = useState('dmc');
  const [threadLibrary, setThreadLibrary] = useState([]);
  const [threadMatches, setThreadMatches] = useState([]);
  
  // Vinyl matching state
  const [vinylProduct, setVinylProduct] = useState('oracal-651');
  const [vinylLibrary, setVinylLibrary] = useState([]);
  const [vinylMatches, setVinylMatches] = useState([]);
  
  // Palettes state
  const [palettes, setPalettes] = useState({});
  const [selectedPalette, setSelectedPalette] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState('seasonal');
  
  // Harmony state
  const [harmonyBase, setHarmonyBase] = useState('#6366f1');
  const [harmonyType, setHarmonyType] = useState('complementary');
  const [harmonyColors, setHarmonyColors] = useState([]);
  
  // Designer colors state
  const [designerColors, setDesignerColors] = useState(null);
  const [designerCategory, setDesignerCategory] = useState('neutrals');
  const [designerSearch, setDesignerSearch] = useState('');
  const [designerSearchResults, setDesignerSearchResults] = useState([]);
  
  // User palettes state
  const [userPalettes, setUserPalettes] = useState([]);
  const [savePaletteName, setSavePaletteName] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [colorsToSave, setColorsToSave] = useState([]);
  
  // Image extraction state
  const [extractedColors, setExtractedColors] = useState([]);
  const [isExtracting, setIsExtracting] = useState(false);
  const fileInputRef = useRef(null);

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  // When panel opens (or craft changes), fetch the list of relevant color libraries
  // for the current craft so the brand dropdown is accurate (DMC for embroidery,
  // Prismacolor for pencil crafts, Bullseye for stained glass, etc.).
  useEffect(() => {
    if (!isOpen) return;
    const key = craftType || 'general';
    fetch(`${API_BASE}/colors/for-craft/${encodeURIComponent(key)}`)
      .then(r => r.json())
      .then(data => {
        const cats = Array.isArray(data.categories) ? data.categories : [];
        setCraftLibraries(cats);
        if (cats.length > 0) {
          setMaterialCategory(cats[0].category);
          const firstBrand = cats[0].brands?.[0]?.id || '';
          setMaterialBrand(firstBrand);
        }
      })
      .catch(() => { /* non-fatal — thread tab still works */ });
  }, [isOpen, craftType]);

  // When material brand changes, load its colors
  useEffect(() => {
    if (!materialCategory || !materialBrand) return;
    fetch(`${API_BASE}/colors/libraries/${materialCategory}/${materialBrand}`)
      .then(r => r.json())
      .then(data => {
        setMaterialLibrary(Array.isArray(data.colors) ? data.colors : []);
      })
      .catch(() => setMaterialLibrary([]));
  }, [materialCategory, materialBrand]);

  // Fetch thread library
  useEffect(() => {
    if (activeTab === 'threads' && threadBrand) {
      fetch(`${API_BASE}/colors/threads/${threadBrand}`)
        .then(res => res.json())
        .then(data => {
          setThreadLibrary(data.colors || []);
          if (canvasColors.length > 0) {
            matchThreads(canvasColors, data.colors || []);
          }
        })
        .catch(err => console.error('Error loading threads:', err));
    }
  }, [activeTab, threadBrand]);

  // Fetch vinyl library
  useEffect(() => {
    if (activeTab === 'vinyl' && vinylProduct) {
      fetch(`${API_BASE}/colors/vinyl/${vinylProduct}`)
        .then(res => res.json())
        .then(data => {
          setVinylLibrary(data.colors || []);
          if (canvasColors.length > 0) {
            matchVinyl(canvasColors, data.colors || []);
          }
        })
        .catch(err => console.error('Error loading vinyl:', err));
    }
  }, [activeTab, vinylProduct]);

  // Fetch craft palettes
  useEffect(() => {
    if (activeTab === 'palettes') {
      fetch(`${API_BASE}/colors/palettes`)
        .then(res => res.json())
        .then(data => setPalettes(data || {}))
        .catch(err => console.error('Error loading palettes:', err));
    }
  }, [activeTab]);

  // Fetch designer colors
  useEffect(() => {
    if (activeTab === 'designer') {
      fetch(`${API_BASE}/colors/designer-colors`)
        .then(res => res.json())
        .then(data => setDesignerColors(data))
        .catch(err => console.error('Error loading designer colors:', err));
    }
  }, [activeTab]);

  // Fetch user palettes
  useEffect(() => {
    if (activeTab === 'my-palettes' && userEmail) {
      fetch(`${API_BASE}/colors/user-palettes?email=${encodeURIComponent(userEmail)}`)
        .then(res => res.json())
        .then(data => setUserPalettes(data.palettes || []))
        .catch(err => console.error('Error loading user palettes:', err));
    }
  }, [activeTab, userEmail]);

  // Generate harmony colors
  useEffect(() => {
    if (activeTab === 'harmony' && harmonyBase) {
      generateHarmony(harmonyBase, harmonyType);
    }
  }, [activeTab, harmonyBase, harmonyType]);

  // Match canvas colors when they change
  useEffect(() => {
    if (canvasColors.length > 0) {
      if (activeTab === 'threads' && threadLibrary.length > 0) {
        matchThreads(canvasColors, threadLibrary);
      }
      if (activeTab === 'vinyl' && vinylLibrary.length > 0) {
        matchVinyl(canvasColors, vinylLibrary);
      }
    }
  }, [canvasColors]);

  // ============================================================================
  // COLOR MATCHING FUNCTIONS
  // ============================================================================

  const matchThreads = (colors, library) => {
    const matches = colors.map(hex => {
      const rgb = hexToRgb(hex);
      if (!rgb) return { hex, matches: [] };
      
      const inputLab = rgbToLab(rgb.r, rgb.g, rgb.b);
      
      const threadMatches = library
        .map(thread => {
          const threadLab = rgbToLab(thread.rgb[0], thread.rgb[1], thread.rgb[2]);
          const diff = deltaE(inputLab, threadLab);
          const matchPercent = Math.max(0, 100 - diff).toFixed(1);
          return { ...thread, matchPercent, diff };
        })
        .sort((a, b) => a.diff - b.diff)
        .slice(0, 5);
      
      return { hex, matches: threadMatches };
    });
    
    setThreadMatches(matches);
  };

  const matchVinyl = (colors, library) => {
    const matches = colors.map(hex => {
      const rgb = hexToRgb(hex);
      if (!rgb) return { hex, matches: [] };
      
      const inputLab = rgbToLab(rgb.r, rgb.g, rgb.b);
      
      const vinylMatches = library
        .map(vinyl => {
          const vinylLab = rgbToLab(vinyl.rgb[0], vinyl.rgb[1], vinyl.rgb[2]);
          const diff = deltaE(inputLab, vinylLab);
          const matchPercent = Math.max(0, 100 - diff).toFixed(1);
          return { ...vinyl, matchPercent, diff };
        })
        .sort((a, b) => a.diff - b.diff)
        .slice(0, 5);
      
      return { hex, matches: vinylMatches };
    });
    
    setVinylMatches(matches);
  };

  // ============================================================================
  // HARMONY GENERATION
  // ============================================================================

  const generateHarmony = (baseHex, type) => {
    const rgb = hexToRgb(baseHex);
    if (!rgb) return;

    // Convert to HSL
    const r = rgb.r / 255, g = rgb.g / 255, b = rgb.b / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
      }
    }

    const hslToHex = (h, s, l) => {
      let r, g, b;
      if (s === 0) {
        r = g = b = l;
      } else {
        const hue2rgb = (p, q, t) => {
          if (t < 0) t += 1;
          if (t > 1) t -= 1;
          if (t < 1/6) return p + (q - p) * 6 * t;
          if (t < 1/2) return q;
          if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
          return p;
        };
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1/3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1/3);
      }
      const toHex = x => {
        const hex = Math.round(x * 255).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      };
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    };

    let colors = [baseHex];
    
    switch (type) {
      case 'complementary':
        colors.push(hslToHex((h + 0.5) % 1, s, l));
        break;
      case 'analogous':
        colors.push(hslToHex((h + 1/12) % 1, s, l));
        colors.push(hslToHex((h - 1/12 + 1) % 1, s, l));
        break;
      case 'triadic':
        colors.push(hslToHex((h + 1/3) % 1, s, l));
        colors.push(hslToHex((h + 2/3) % 1, s, l));
        break;
      case 'split-complementary':
        colors.push(hslToHex((h + 5/12) % 1, s, l));
        colors.push(hslToHex((h + 7/12) % 1, s, l));
        break;
      case 'tetradic':
        colors.push(hslToHex((h + 0.25) % 1, s, l));
        colors.push(hslToHex((h + 0.5) % 1, s, l));
        colors.push(hslToHex((h + 0.75) % 1, s, l));
        break;
      case 'monochromatic':
        colors.push(hslToHex(h, s, Math.min(l + 0.2, 1)));
        colors.push(hslToHex(h, s, Math.max(l - 0.2, 0)));
        colors.push(hslToHex(h, s, Math.min(l + 0.35, 1)));
        colors.push(hslToHex(h, s, Math.max(l - 0.35, 0)));
        break;
      case 'shades':
        for (let i = 1; i <= 4; i++) {
          colors.push(hslToHex(h, s, Math.max(l - i * 0.15, 0.05)));
        }
        break;
      case 'tints':
        for (let i = 1; i <= 4; i++) {
          colors.push(hslToHex(h, s, Math.min(l + i * 0.15, 0.95)));
        }
        break;
    }

    setHarmonyColors(colors);
  };

  // ============================================================================
  // USER PALETTE FUNCTIONS
  // ============================================================================

  const savePalette = async (name, colors, source = 'manual') => {
    if (!userEmail || !name || colors.length === 0) return;
    
    try {
      const res = await fetch(`${API_BASE}/colors/user-palettes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail, name, colors, source })
      });
      
      const data = await res.json();
      if (data.palette) {
        setUserPalettes(prev => [data.palette, ...prev]);
        setShowSaveModal(false);
        setSavePaletteName('');
        setColorsToSave([]);
      }
    } catch (err) {
      console.error('Error saving palette:', err);
    }
  };

  const deletePalette = async (id) => {
    if (!userEmail) return;
    
    try {
      await fetch(`${API_BASE}/colors/user-palettes/${id}?email=${encodeURIComponent(userEmail)}`, {
        method: 'DELETE'
      });
      setUserPalettes(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      console.error('Error deleting palette:', err);
    }
  };

  const openSaveModal = (colors) => {
    setColorsToSave(colors);
    setShowSaveModal(true);
  };

  // ============================================================================
  // IMAGE EXTRACTION
  // ============================================================================

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsExtracting(true);
    
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result;
        
        const res = await fetch(`${API_BASE}/colors/extract-from-image`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ imageData: base64, numColors: 6 })
        });
        
        const data = await res.json();
        if (data.colors) {
          setExtractedColors(data.colors);
        }
        setIsExtracting(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Error extracting colors:', err);
      setIsExtracting(false);
    }
  };

  // ============================================================================
  // DESIGNER COLORS SEARCH
  // ============================================================================

  useEffect(() => {
    if (designerSearch.length >= 2) {
      fetch(`${API_BASE}/colors/designer-colors-search?q=${encodeURIComponent(designerSearch)}`)
        .then(res => res.json())
        .then(data => setDesignerSearchResults(data.results || []))
        .catch(err => console.error('Error searching:', err));
    } else {
      setDesignerSearchResults([]);
    }
  }, [designerSearch]);

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  const copyColor = (hex) => {
    navigator.clipboard.writeText(hex);
    setCopiedColor(hex);
    setTimeout(() => setCopiedColor(null), 2000);
  };

  if (!isOpen) return null;

  // ============================================================================
  // COLLAPSED STATE
  // ============================================================================

  if (isCollapsed) {
    return (
      <div className="fixed inset-y-0 right-0 w-12 bg-white dark:bg-slate-800 shadow-2xl z-50 flex flex-col items-center py-4 border-l border-slate-200 dark:border-slate-700">
        <button
          onClick={() => setIsCollapsed(false)}
          className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg mb-4"
          title="Expand Color Tools"
        >
          <PanelRight className="w-5 h-5 text-zenna-violet" />
        </button>
        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-zenna-violet to-pink-500" />
        <span className="text-xs text-slate-500 mt-2" style={{ writingMode: 'vertical-rl' }}>
          Colors
        </span>
      </div>
    );
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="fixed inset-y-0 right-0 w-96 bg-white dark:bg-slate-800 shadow-2xl z-50 flex flex-col border-l border-slate-200 dark:border-slate-700">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
        <h2 className="text-lg font-semibold text-slate-800 dark:text-white flex items-center gap-2">
          <Palette className="w-5 h-5 text-zenna-violet" />
          Color Tools
        </h2>
        <div className="flex items-center gap-1">
          <button 
            onClick={() => setIsCollapsed(true)} 
            className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
            title="Collapse panel"
          >
            <PanelRightClose className="w-5 h-5 text-slate-500" />
          </button>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
      </div>

      {/* Tabs - Two Rows */}
      <div className="border-b border-slate-200 dark:border-slate-700">
        {/* Row 1: Threads, Vinyl, Palettes */}
        <div className="flex">
          {[
            { id: 'threads', label: 'Threads', icon: '🧵' },
            { id: 'vinyl', label: 'Vinyl', icon: '✂️' },
            { id: 'palettes', label: 'Palettes', icon: '🎨' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-zenna-violet border-b-2 border-zenna-violet bg-zenna-violet/5'
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              <span className="mr-1">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
        {/* Row 2: Designer, Harmony, My Colors */}
        <div className="flex">
          {[
            { id: 'designer', label: 'Designer', icon: '📐' },
            { id: 'harmony', label: 'Harmony', icon: '🌈' },
            { id: 'my-palettes', label: 'My Colors', icon: '💜' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-zenna-violet border-b-2 border-zenna-violet bg-zenna-violet/5'
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              <span className="mr-1">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        
        {/* ============================================================ */}
        {/* THREADS TAB */}
        {/* ============================================================ */}
        {activeTab === 'threads' && (
          <div className="space-y-4">
            {/* Craft-aware materials block — shows the right library for the current craft
                (pencils for coloring pages, yarn for knitting, glass for stained glass, etc.) */}
            {craftLibraries.length > 0 && (
              <div className="bg-zenna-violet/5 border border-zenna-violet/20 rounded-xl p-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-zenna-violet mb-2">
                  Materials for {craftType ? craftType.replace(/_/g, ' ') : 'this craft'}
                </div>
                {craftLibraries.length > 1 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {craftLibraries.map(cat => (
                      <button
                        key={cat.category}
                        onClick={() => {
                          setMaterialCategory(cat.category);
                          setMaterialBrand(cat.brands?.[0]?.id || '');
                        }}
                        className={`px-2 py-1 rounded-full text-[11px] font-medium ${
                          materialCategory === cat.category
                            ? 'bg-zenna-violet text-white'
                            : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-300'
                        }`}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                )}
                <select
                  value={materialBrand}
                  onChange={e => setMaterialBrand(e.target.value)}
                  className="w-full p-2 border rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white text-sm mb-2"
                >
                  {(craftLibraries.find(c => c.category === materialCategory)?.brands || []).map(b => (
                    <option key={b.id} value={b.id}>{b.name} ({b.colorCount})</option>
                  ))}
                </select>
                {materialLibrary.length > 0 && (
                  <div className="max-h-56 overflow-y-auto">
                    <div className="grid grid-cols-8 gap-1">
                      {materialLibrary.map((c, i) => (
                        <button
                          key={`${c.code}-${i}`}
                          onClick={() => copyColor(c.hex)}
                          className="aspect-square rounded border border-slate-300 dark:border-slate-600 relative group"
                          style={{ backgroundColor: c.hex }}
                          title={`${c.code || ''} ${c.name || ''} — ${c.hex}`}
                        >
                          {copiedColor === c.hex && (
                            <Check className="w-3 h-3 text-white absolute inset-0 m-auto drop-shadow" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Thread Brand (for embroidery matching)
              </label>
              <select
                value={threadBrand}
                onChange={(e) => setThreadBrand(e.target.value)}
                className="w-full p-2 border rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white"
              >
                <option value="dmc">DMC Embroidery Floss</option>
                <option value="isacord">Isacord 40wt Polyester</option>
                <option value="madeira">Madeira Rayon 40</option>
                <option value="sulky">Sulky Rayon 40wt</option>
              </select>
            </div>

            {canvasColors.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Design Colors
                  </h3>
                  <button
                    onClick={() => openSaveModal(canvasColors)}
                    className="text-xs text-zenna-violet hover:underline flex items-center gap-1"
                  >
                    <Save className="w-3 h-3" /> Save
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {canvasColors.map((color, i) => (
                    <button
                      key={i}
                      onClick={() => copyColor(color)}
                      className="w-8 h-8 rounded-lg shadow-sm border border-slate-200 dark:border-slate-600 relative group"
                      style={{ backgroundColor: color }}
                      title={color}
                    >
                      {copiedColor === color && (
                        <Check className="w-4 h-4 text-white absolute inset-0 m-auto drop-shadow" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {threadMatches.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Thread Matches
                </h3>
                {threadMatches.map((colorMatch, i) => (
                  <div key={i} className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className="w-6 h-6 rounded shadow-sm border"
                        style={{ backgroundColor: colorMatch.hex }}
                      />
                      <span className="text-xs font-mono text-slate-600 dark:text-slate-400">
                        {colorMatch.hex}
                      </span>
                    </div>
                    <div className="space-y-1">
                      {colorMatch.matches.map((thread, j) => (
                        <div
                          key={j}
                          onClick={() => copyColor(thread.hex)}
                          className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 p-1 rounded"
                        >
                          <div
                            className="w-4 h-4 rounded shadow-sm"
                            style={{ backgroundColor: thread.hex }}
                          />
                          <span className="font-medium text-slate-700 dark:text-slate-300">
                            {thread.code}
                          </span>
                          <span className="text-slate-500 dark:text-slate-400 flex-1 truncate">
                            {thread.name}
                          </span>
                          <span className={`font-medium ${
                            parseFloat(thread.matchPercent) >= 95 ? 'text-green-600' :
                            parseFloat(thread.matchPercent) >= 85 ? 'text-yellow-600' :
                            'text-orange-600'
                          }`}>
                            {thread.matchPercent}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {canvasColors.length === 0 && (
              <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-8">
                Generate or select a design to see thread matches
              </p>
            )}
          </div>
        )}

        {/* ============================================================ */}
        {/* VINYL TAB */}
        {/* ============================================================ */}
        {activeTab === 'vinyl' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Vinyl Product
              </label>
              <select
                value={vinylProduct}
                onChange={(e) => setVinylProduct(e.target.value)}
                className="w-full p-2 border rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white"
              >
                <option value="oracal-651">Oracal 651 Permanent Glossy</option>
                <option value="oracal-631">Oracal 631 Matte Removable</option>
                <option value="siser-easyweed">Siser EasyWeed HTV</option>
                <option value="cricut-vinyl">Cricut Premium Vinyl</option>
              </select>
            </div>

            {canvasColors.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Design Colors
                  </h3>
                  <button
                    onClick={() => openSaveModal(canvasColors)}
                    className="text-xs text-zenna-violet hover:underline flex items-center gap-1"
                  >
                    <Save className="w-3 h-3" /> Save
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {canvasColors.map((color, i) => (
                    <button
                      key={i}
                      onClick={() => copyColor(color)}
                      className="w-8 h-8 rounded-lg shadow-sm border border-slate-200 dark:border-slate-600 relative"
                      style={{ backgroundColor: color }}
                      title={color}
                    >
                      {copiedColor === color && (
                        <Check className="w-4 h-4 text-white absolute inset-0 m-auto drop-shadow" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {vinylMatches.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Vinyl Matches
                </h3>
                {vinylMatches.map((colorMatch, i) => (
                  <div key={i} className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className="w-6 h-6 rounded shadow-sm border"
                        style={{ backgroundColor: colorMatch.hex }}
                      />
                      <span className="text-xs font-mono text-slate-600 dark:text-slate-400">
                        {colorMatch.hex}
                      </span>
                    </div>
                    <div className="space-y-1">
                      {colorMatch.matches.map((vinyl, j) => (
                        <div
                          key={j}
                          onClick={() => copyColor(vinyl.hex)}
                          className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 p-1 rounded"
                        >
                          <div
                            className="w-4 h-4 rounded shadow-sm"
                            style={{ backgroundColor: vinyl.hex }}
                          />
                          <span className="font-medium text-slate-700 dark:text-slate-300">
                            {vinyl.code}
                          </span>
                          <span className="text-slate-500 dark:text-slate-400 flex-1 truncate">
                            {vinyl.name}
                          </span>
                          {vinyl.metallic && (
                            <span className="text-yellow-500">✨</span>
                          )}
                          <span className={`font-medium ${
                            parseFloat(vinyl.matchPercent) >= 95 ? 'text-green-600' :
                            parseFloat(vinyl.matchPercent) >= 85 ? 'text-yellow-600' :
                            'text-orange-600'
                          }`}>
                            {vinyl.matchPercent}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {canvasColors.length === 0 && (
              <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-8">
                Generate or select a design to see vinyl matches
              </p>
            )}
          </div>
        )}

        {/* ============================================================ */}
        {/* PALETTES TAB */}
        {/* ============================================================ */}
        {activeTab === 'palettes' && (
          <div className="space-y-4">
            {Object.entries(palettes).map(([category, categoryPalettes]) => (
              <div key={category}>
                <button
                  onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
                  className="flex items-center justify-between w-full text-left p-2 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-lg"
                >
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300 capitalize">
                    {category}
                  </span>
                  {expandedCategory === category ? (
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  )}
                </button>
                
                {expandedCategory === category && (
                  <div className="space-y-2 mt-2 pl-2">
                    {Object.entries(categoryPalettes).map(([name, colors]) => (
                      <div
                        key={name}
                        className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-slate-600 dark:text-slate-400 capitalize">
                            {name.replace(/-/g, ' ')}
                          </span>
                          <button
                            onClick={() => openSaveModal(colors)}
                            className="text-xs text-zenna-violet hover:underline"
                          >
                            Save
                          </button>
                        </div>
                        <div className="flex gap-1">
                          {colors.map((color, i) => (
                            <button
                              key={i}
                              onClick={() => copyColor(color)}
                              className="flex-1 h-8 rounded shadow-sm relative group"
                              style={{ backgroundColor: color }}
                              title={color}
                            >
                              {copiedColor === color && (
                                <Check className="w-3 h-3 text-white absolute inset-0 m-auto drop-shadow" />
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ============================================================ */}
        {/* DESIGNER COLORS TAB */}
        {/* ============================================================ */}
        {activeTab === 'designer' && (
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={designerSearch}
                onChange={(e) => setDesignerSearch(e.target.value)}
                placeholder="Search by name or code..."
                className="w-full pl-9 pr-3 py-2 border rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white text-sm"
              />
            </div>

            {/* Search Results */}
            {designerSearchResults.length > 0 && (
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3 space-y-1">
                <h4 className="text-xs font-medium text-slate-500 mb-2">Search Results</h4>
                {designerSearchResults.map((color, i) => (
                  <div
                    key={i}
                    onClick={() => copyColor(color.hex)}
                    className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600 p-1 rounded"
                  >
                    <div
                      className="w-6 h-6 rounded shadow-sm"
                      style={{ backgroundColor: color.hex }}
                    />
                    <span className="font-mono font-medium text-slate-700 dark:text-slate-300">
                      {color.code}
                    </span>
                    <span className="text-slate-500 dark:text-slate-400 flex-1">
                      {color.name}
                    </span>
                    {copiedColor === color.hex && <Check className="w-3 h-3 text-green-500" />}
                  </div>
                ))}
              </div>
            )}

            {/* Category Selector */}
            {designerColors && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Category
                  </label>
                  <select
                    value={designerCategory}
                    onChange={(e) => setDesignerCategory(e.target.value)}
                    className="w-full p-2 border rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white"
                  >
                    {Object.entries(designerColors.categories).map(([key, cat]) => (
                      <option key={key} value={key}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                {/* Colors Grid */}
                <div className="space-y-1">
                  {designerColors.categories[designerCategory]?.colors.map((color, i) => (
                    <div
                      key={i}
                      onClick={() => copyColor(color.hex)}
                      className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 p-2 rounded-lg"
                    >
                      <div
                        className="w-8 h-8 rounded shadow-sm border border-slate-200 dark:border-slate-600"
                        style={{ backgroundColor: color.hex }}
                      />
                      <div className="flex-1">
                        <div className="font-mono font-medium text-slate-700 dark:text-slate-300">
                          {color.code}
                        </div>
                        <div className="text-slate-500 dark:text-slate-400">
                          {color.name}
                        </div>
                      </div>
                      {color.metallic && <span className="text-yellow-500">✨</span>}
                      {copiedColor === color.hex ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4 text-slate-400" />
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* ============================================================ */}
        {/* HARMONY TAB */}
        {/* ============================================================ */}
        {activeTab === 'harmony' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Base Color
              </label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={harmonyBase}
                  onChange={(e) => setHarmonyBase(e.target.value)}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={harmonyBase}
                  onChange={(e) => setHarmonyBase(e.target.value)}
                  className="flex-1 p-2 border rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white font-mono text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Harmony Type
              </label>
              <select
                value={harmonyType}
                onChange={(e) => setHarmonyType(e.target.value)}
                className="w-full p-2 border rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white"
              >
                <option value="complementary">Complementary</option>
                <option value="analogous">Analogous</option>
                <option value="triadic">Triadic</option>
                <option value="split-complementary">Split Complementary</option>
                <option value="tetradic">Tetradic (Square)</option>
                <option value="monochromatic">Monochromatic</option>
                <option value="shades">Shades (Darker)</option>
                <option value="tints">Tints (Lighter)</option>
              </select>
            </div>

            {harmonyColors.length > 0 && (
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Generated Palette
                  </h3>
                  <button
                    onClick={() => openSaveModal(harmonyColors)}
                    className="text-xs text-zenna-violet hover:underline flex items-center gap-1"
                  >
                    <Save className="w-3 h-3" /> Save
                  </button>
                </div>
                <div className="flex gap-1 mb-3">
                  {harmonyColors.map((color, i) => (
                    <button
                      key={i}
                      onClick={() => copyColor(color)}
                      className="flex-1 h-16 rounded-lg shadow-sm relative group"
                      style={{ backgroundColor: color }}
                    >
                      {copiedColor === color && (
                        <Check className="w-4 h-4 text-white absolute inset-0 m-auto drop-shadow" />
                      )}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  {harmonyColors.map((color, i) => (
                    <span
                      key={i}
                      className="text-xs font-mono text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-600 px-2 py-1 rounded cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-500"
                      onClick={() => copyColor(color)}
                    >
                      {color}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ============================================================ */}
        {/* MY PALETTES TAB */}
        {/* ============================================================ */}
        {activeTab === 'my-palettes' && (
          <div className="space-y-4">
            {/* Image Upload for Extraction */}
            <div className="bg-gradient-to-r from-zenna-violet/10 to-pink-500/10 rounded-lg p-4">
              <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
                <Image className="w-4 h-4" />
                Extract from Image
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                Upload an image to extract its color palette
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isExtracting}
                className="w-full py-2 bg-zenna-violet text-white rounded-lg text-sm font-medium hover:bg-zenna-violet/90 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isExtracting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Extracting...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload Image
                  </>
                )}
              </button>
              
              {extractedColors.length > 0 && (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-slate-600 dark:text-slate-400">Extracted Colors</span>
                    <button
                      onClick={() => openSaveModal(extractedColors)}
                      className="text-xs text-zenna-violet hover:underline"
                    >
                      Save as Palette
                    </button>
                  </div>
                  <div className="flex gap-1">
                    {extractedColors.map((color, i) => (
                      <button
                        key={i}
                        onClick={() => copyColor(color)}
                        className="flex-1 h-10 rounded shadow-sm relative"
                        style={{ backgroundColor: color }}
                        title={color}
                      >
                        {copiedColor === color && (
                          <Check className="w-3 h-3 text-white absolute inset-0 m-auto drop-shadow" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Saved Palettes */}
            <div>
              <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                <Heart className="w-4 h-4" />
                Saved Palettes ({userPalettes.length}/50)
              </h3>
              
              {userPalettes.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-8">
                  No saved palettes yet. Save colors from other tabs!
                </p>
              ) : (
                <div className="space-y-3">
                  {userPalettes.map((palette) => (
                    <div
                      key={palette.id}
                      className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                          {palette.name}
                        </span>
                        <button
                          onClick={() => deletePalette(palette.id)}
                          className="text-slate-400 hover:text-red-500 p-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex gap-1">
                        {palette.colors.map((color, i) => (
                          <button
                            key={i}
                            onClick={() => copyColor(color)}
                            className="flex-1 h-8 rounded shadow-sm relative"
                            style={{ backgroundColor: color }}
                            title={color}
                          >
                            {copiedColor === color && (
                              <Check className="w-3 h-3 text-white absolute inset-0 m-auto drop-shadow" />
                            )}
                          </button>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                        <span className="capitalize">{palette.source}</span>
                        <span>•</span>
                        <span>{new Date(palette.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer hint */}
      <div className="p-3 border-t border-slate-200 dark:border-slate-700 text-center">
        <p className="text-xs text-slate-400">Click any color to copy its hex code ✨</p>
      </div>

      {/* Save Palette Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 w-80 shadow-2xl">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">
              Save Palette
            </h3>
            <div className="flex gap-1 mb-4">
              {colorsToSave.map((color, i) => (
                <div
                  key={i}
                  className="flex-1 h-10 rounded shadow-sm"
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
            <input
              type="text"
              value={savePaletteName}
              onChange={(e) => setSavePaletteName(e.target.value)}
              placeholder="Palette name..."
              className="w-full p-2 border rounded-lg mb-4 bg-white dark:bg-slate-700 text-slate-800 dark:text-white"
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowSaveModal(false);
                  setSavePaletteName('');
                  setColorsToSave([]);
                }}
                className="flex-1 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700"
              >
                Cancel
              </button>
              <button
                onClick={() => savePalette(savePaletteName, colorsToSave, 'manual')}
                disabled={!savePaletteName.trim()}
                className="flex-1 py-2 bg-zenna-violet text-white rounded-lg hover:bg-zenna-violet/90 disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}